import axios, { AxiosError, AxiosResponse } from 'axios';
import i18n, { resolveLanguageCode } from '../i18n';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

let authToken: string | null = null;
let messagesContext: { addError: (message: string) => void } | null = null;

export function setAuthToken(token: string | null) {
    authToken = token;
}

export function setMessagesContext(context: { addError: (message: string) => void }) {
    messagesContext = context;
}

export const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
});

api.interceptors.request.use((config) => {
    if (authToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${authToken}`;
    }
    const locale = resolveLanguageCode(i18n.language);
    config.headers = config.headers || {};
    config.headers['Accept-Language'] = locale;
    config.headers['X-Locale'] = locale;
    return config;
});

api.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>) => {
        if (messagesContext) {
            const errorMessage = extractErrorMessage(error);
            messagesContext.addError(errorMessage);
        }
        return Promise.reject(error);
    }
);

function extractErrorMessage(error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>): string {
    if (!error.response) {
        return error.message || 'Network error occurred';
    }

    const { data, status } = error.response;

    if (data?.message) {
        return data.message;
    }

    if (data?.errors && typeof data.errors === 'object') {
        const errorMessages = Object.values(data.errors)
            .flat()
            .filter(Boolean)
            .join(', ');
        if (errorMessages) {
            return errorMessages;
        }
    }

    if (status === 422) {
        return 'Validation error occurred';
    }

    if (status === 401) {
        return 'Unauthorized. Please login again.';
    }

    if (status === 403) {
        return 'Forbidden. You do not have permission.';
    }

    if (status === 404) {
        return 'Resource not found';
    }

    if (status >= 500) {
        return 'Server error occurred. Please try again later.';
    }

    return error.message || 'An error occurred';
}

export type ApiResponse<T> = {
    data: T;
};

export function extractData<T = any>(response: AxiosResponse): T {
    if (response.data?.data) {
        return response.data.data;
    }
    return response.data;
}


